import React, { useEffect, useMemo, useRef, useState } from "react";
import { Modal, Spinner } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { closeProfileModal } from "../../../app/redux/features/profile/profileSlice";
import { Field, FormikProvider, useFormik } from "formik";
import { Autocomplete } from "@react-google-maps/api";
import FieldInputText from "../../../app/modules/common/FieldInputText";
import SubmitButton from "../../../app/modules/common/SubmitButton";
import * as Yup from 'yup'
import { ACCOUNT_MARGIN_MAX, ACCOUNT_MARGIN_MIN, ACCOUNT_MARGIN_NUMBER, ACCOUNT_MARGIN_REQUIRED, CITY_REQUIRED, COMPANY_NAME_REQUIRED, COUNTRY_REQUIRED, EMAIL_REQUIRED, INVALID_COUNTRY, INVALID_EMAIL_MESSAGE, INVALID_PEC_NUMBER, INVALID_PHONE_NUMBER, INVALID_PROVINCE_CODE, INVALID_SDI_NUMBER, INVALID_VAT_NUMBER, PASSWORD_LENGTH_MESSAGE, PASSWORD_REQUIRED, PEC_REQUIRED, PHONE_NUMBER_REQUIRED, PROVINCE_CODE_REQUIRED, SDI_REQUIRED, SHOP_ADDRESS_REQUIRED, VAT_REQUIRED, ZIP_CODE_REQUIRED } from "../../../app/utlis/messages";
import { GetProfileDetails, updateProfile } from "../../../app/redux/features/profile/profileActions";
import { ISO_COUNTRIES } from "../../../app/utlis/isoCountryCodes";

const VAT_REGEX = /^$|^([Ii][Tt])?(\d{11})$|^(?![iI][tT])([a-zA-Z]{2,3})(\d+)$/;
const PEC_REGEX = /^$|^([^ @]+)@([^ @]+)\.([a-zA-Z]{2,})$/;
const SDI_REGEX = /^$|^([\w\d]{7})$/;


const validationSchema = Yup.object({
  email: Yup.string().email(INVALID_EMAIL_MESSAGE).required(EMAIL_REQUIRED),
  shopAddress: Yup.string().required(SHOP_ADDRESS_REQUIRED),
  companyName: Yup.string().required(COMPANY_NAME_REQUIRED),
  vat: Yup.string().required(VAT_REQUIRED).matches(VAT_REGEX, INVALID_VAT_NUMBER),
  street: Yup.string(),
  houseNumber: Yup.string(),
  zipCode: Yup.string().required(ZIP_CODE_REQUIRED),
  country: Yup.string().required(COUNTRY_REQUIRED).oneOf(ISO_COUNTRIES, INVALID_COUNTRY),
  city: Yup.string().required(CITY_REQUIRED),
  provinceCode: Yup.string().required(PROVINCE_CODE_REQUIRED).matches(/^[A-Z]{2}$/, INVALID_PROVINCE_CODE),
  phone: Yup.string().required(PHONE_NUMBER_REQUIRED).matches(/^\+?\d{7,15}$/, INVALID_PHONE_NUMBER),
  accountTargetMargin: Yup.number().required(ACCOUNT_MARGIN_REQUIRED).typeError(ACCOUNT_MARGIN_NUMBER).min(0, ACCOUNT_MARGIN_MIN).max(100, ACCOUNT_MARGIN_MAX),
  pec: Yup.string().matches(PEC_REGEX, INVALID_PEC_NUMBER),
  sdi: Yup.string().required(SDI_REQUIRED).matches(SDI_REGEX, INVALID_SDI_NUMBER),
});


const ProfileModal = () => {
  const dispatch: any = useDispatch();
  const { openProfileDetailModal, loading } = useSelector(
    (state: any) => state.profileDetailsSlice
  );
  const autocompleteRef = useRef<any>(null);
  const [profileDetails, setProfileDetails] = useState<any>(null);

  // Fetch profile details on modal open
  useEffect(() => {
    if (openProfileDetailModal) {
      dispatch(GetProfileDetails()).then((response) => {
        setProfileDetails(response?.payload?.data);
      }).catch((error) => {
        console.error("Failed to fetch profile details:", error);
      });
    }
  }, [openProfileDetailModal]);

  // Memoized initial values
  const initialValues = useMemo(() => ({
    email: profileDetails?.email || '',
    shopAddress: profileDetails?.shopAddress || '',
    companyName: profileDetails?.companyName || '',
    vat: profileDetails?.vat || '',
    street: profileDetails?.street || '',
    houseNumber: profileDetails?.houseNumber || '',
    zipCode: profileDetails?.zipCode || '',
    country: profileDetails?.country || '',
    city: profileDetails?.city || '',
    provinceCode: profileDetails?.provinceCode || '',
    phone: profileDetails?.phone || '',
    accountTargetMargin: profileDetails?.accountTargetMargin || 0,
    pec: profileDetails?.pec || '',
    sdi: profileDetails?.sdi || '',
  }), [profileDetails]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema: validationSchema,
    validate: async (values) => {
      if (!values.shopAddress) {
        return { shopAddress: SHOP_ADDRESS_REQUIRED };
      }
    },
    onSubmit: async (values) => {
      try {
        await dispatch(updateProfile({ values }));
        handleClose();
      } catch (error) {
        console.error("Registration failed:", error);
      }
    },
  });

  // Detect modal close and reset form
  const prevOpenRef = useRef<boolean>(openProfileDetailModal);
  useEffect(() => {
    if (prevOpenRef.current && !openProfileDetailModal) {
      formik.resetForm();
    }
    prevOpenRef.current = openProfileDetailModal;
  }, [openProfileDetailModal]);

  const handleClose = () => {
    dispatch(closeProfileModal());
  };

  const handlePlaceChanged = () => {
    const autocomplete = autocompleteRef.current;
    if (!autocomplete) return;

    const place = autocomplete.getPlace();
    if (!place.geometry || !place.address_components) return;

    const components = place.address_components;
    console.log('components ====== ', components);

    const getComponent = (type: string, short = false) => {
      const comp = components.find((c: any) => c.types.includes(type));
      if (!comp) return '';
      return short ? comp.short_name : comp.long_name;
    };

    // Extracting desired fields
    const streetNumber = getComponent('street_number');
    const route = getComponent('route');
    const sublocality = getComponent('sublocality') || getComponent('sublocality_level_1');
    const neighborhood = getComponent('neighborhood');
    const premise = getComponent('premise');
    const establishment = getComponent('establishment');

    // Optional building name or business name
    const name = place.name;

    // Use the most specific, human-readable parts
    const rawAddressParts = [
      name,
      premise,
      establishment,
      neighborhood,
      sublocality,
    ];

    const uniqueParts = Array.from(new Set(rawAddressParts.filter(Boolean))); // removes duplicates and falsy values
    const simplifiedAddress = uniqueParts.join(', ');

    // Full address info
    const zipCode = getComponent('postal_code');
    const city = getComponent('locality') || getComponent('administrative_area_level_2');
    const provinceShort = getComponent('administrative_area_level_2', true) || getComponent('administrative_area_level_1', true);
    let countryCode = getComponent('country', true);
    const countryName = getComponent('country', false);

    // Update Formik values
    formik.setFieldValue('shopAddress', simplifiedAddress);
    if (zipCode) formik.setFieldValue('zipCode', zipCode);
    if (city) formik.setFieldValue('city', city);
    if (countryCode) formik.setFieldValue('country', countryCode.toUpperCase());
    if (route) formik.setFieldValue('street', route); // Via
    if (streetNumber) formik.setFieldValue('houseNumber', streetNumber); // Numero Civico

    const upperProvince = provinceShort?.toUpperCase();
    if (countryCode?.toUpperCase() === 'IT' && upperProvince && /^[A-Z]{2}$/.test(upperProvince)) {
      formik.setFieldValue('provinceCode', upperProvince);
    } else {
      formik.setFieldValue('provinceCode', '');
    }
  };


  return (
    <Modal show={openProfileDetailModal} onHide={handleClose} centered size="lg">
      <Modal.Header closeButton className="bg-dark text-white position-relative">
        <Modal.Title>Dettagli del profilo</Modal.Title>
      </Modal.Header>


      <Modal.Body>
        {loading ? (
          <div className="d-flex justify-content-center py-5">
            <Spinner animation="border" variant="primary" />
          </div>
        ) : (
          <FormikProvider value={formik}>
            <form onSubmit={formik.handleSubmit} noValidate>

              <div className="row">
                <div className="col-md-6">
                  <Field name="email" label="E-mail" required component={FieldInputText} readOnly />
                </div>

                <div className="col-md-6">
                  <Field name="phone" label="Telefono" required component={FieldInputText} />
                </div>

                <div className="col-md-6">
                  <Field name="companyName" label="Ragione sociale" required component={FieldInputText} hint="*inserire ragione sociale della propria attività" />
                </div>

                <div className="col-md-6">
                  <Field name="vat" label="Partita Iva" required component={FieldInputText} hint="*inserisci partita iva senza spazi es. 00154130207" />
                </div>

                <div className="col-md-6">
                  <label className='form-label required'><strong>Indirizzo del negozio</strong></label>
                  <div className="form-text text-muted mb-1">
                    *selezionare da menu suggerimenti a tendina per assicurarsi di completare correttamente il campo
                  </div>
                  <Autocomplete onLoad={(ref) => (autocompleteRef.current = ref)} onPlaceChanged={handlePlaceChanged}>
                    <input
                      type="text"
                      name="shopAddress"
                      className="commonInput form-control"
                      placeholder="Indirizzo del negozio"
                      value={formik.values.shopAddress}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                    />
                  </Autocomplete>
                  {formik.touched.shopAddress && typeof formik.errors.shopAddress === "string" && (
                    <div className="text-danger">{formik.errors.shopAddress}</div>
                  )}
                </div>

                <div className="col-md-6">
                  <Field name="street" label="Via" component={FieldInputText} />
                </div>
                <div className="col-md-6">
                  <Field name="houseNumber" label="Numero Civico" component={FieldInputText} />
                </div>

                <div className="col-md-6">
                  <Field name="zipCode" label="CAP" required component={FieldInputText} />
                </div>
                <div className="col-md-6">
                  <Field name="country" label="Codice Stato" required component={FieldInputText} hint="inserisci il codice es. italia inserire IT" />
                </div>

                <div className="col-md-6">
                  <Field name="city" label="Città" required component={FieldInputText} />
                </div>
                <div className="col-md-6">
                  <Field name="provinceCode" label="Codice Provincia" required component={FieldInputText} hint="*inserisci il codice provincia es. Mantova inserisci MN" />
                </div>

                <div className="col-md-6">
                  <Field name="pec" label="PEC" type="string" component={FieldInputText} placeholder="PEC" hint="*inserisci l'indirizzo pec per la fatturazione elettronica" />
                </div>
                <div className="col-md-6">
                  <Field name="sdi" label="SDI" type="string" required component={FieldInputText} placeholder="SDI" hint="*inserisci il tuo codice sdi per la fatturazione elettronica" />
                </div>

                <div className="col-md-6">
                  <Field name="accountTargetMargin" label="Margine previsto del conto (%)" type="number" required component={FieldInputText} hint="*Inserisci la percentuale di margine che desideri ottenere nel tuo account da 1% a 100%. Più competitiva sarà la tua offerta e maggiori saranno le tue possibilità di ricevere ordini. Consideralo come un sistema di aste: a parità di condizioni, i rivenditori con le offerte migliori hanno la priorità." />
                </div>
              </div>

              <div className="d-grid mt-3">
                <SubmitButton title="Salva modifiche" type="submit" loading={false} />
              </div>
            </form>
          </FormikProvider>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default ProfileModal;
